/**
 * DOCUMENTS PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * File storage with S3 support and linkage to business/project/task/category
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  FunnelIcon,
  DocumentIcon,
  DocumentTextIcon,
  PhotoIcon,
  TableCellsIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Screen, Section, Button, Select, Input, Panel } from '../design/primitives';
import { SPACING, COLORS, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { apiClient } from '../api/apiConfig';
import { Document, DocumentUploadResponse } from '../types/document';
import { Spinner, Alert } from '../components/ui';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';

interface FilterOptions {
  businessId: string;
  projectId: string;
  taskId: string;
  categoryId: string;
  search: string;
}

const getFileIcon = (fileType: string) => {
  if (fileType.startsWith('image/')) {
    return <PhotoIcon style={{ width: '24px', height: '24px' }} />;
  }
  if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType === 'text/csv') {
    return <TableCellsIcon style={{ width: '24px', height: '24px' }} />;
  }
  if (fileType === 'application/pdf' || fileType.includes('document') || fileType.includes('word')) {
    return <DocumentTextIcon style={{ width: '24px', height: '24px' }} />;
  }
  return <DocumentIcon style={{ width: '24px', height: '24px' }} />;
};

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasProcessedParams = useRef(false);

  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    businessId: '',
    projectId: '',
    taskId: '',
    categoryId: '',
    search: '',
  });

  const [businesses, setBusinesses] = useState<Array<{ id: number; name: string }>>([]);
  const [projects, setProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [tasks, setTasks] = useState<Array<{ id: number; description: string }>>([]);
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);

  // Upload modal state
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadBusinessId, setUploadBusinessId] = useState('');
  const [uploadProjectId, setUploadProjectId] = useState('');
  const [uploadTaskId, setUploadTaskId] = useState('');
  const [uploadCategoryIds, setUploadCategoryIds] = useState<number[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Preview modal state
  const [previewDoc, setPreviewDoc] = useState<Document | null>(null);

  // Delete modal state
  const [deleteDoc, setDeleteDoc] = useState<Document | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.businessId) params.append('business', filters.businessId);
      if (filters.projectId) params.append('project', filters.projectId);
      if (filters.taskId) params.append('task', filters.taskId);
      if (filters.categoryId) params.append('category', filters.categoryId);
      if (filters.search) params.append('search', filters.search);

      const response = await apiClient.get(`/api/documents/?${params.toString()}`);
      setDocuments(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments, refreshKey]);

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [businessesRes, projectsRes, categoriesRes, tasksRes] = await Promise.all([
          apiClient.get('/api/businesses/'),
          apiClient.get('/api/projects/'),
          apiClient.get('/api/categories/'),
          apiClient.get('/api/tasks/?is_done=false'),
        ]);
        setBusinesses(businessesRes.data);
        setProjects(projectsRes.data);
        setCategories(categoriesRes.data);
        setTasks(tasksRes.data.slice(0, 50)); // Limit to 50 recent tasks
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      businessId: '',
      projectId: '',
      taskId: '',
      categoryId: '',
      search: '',
    });
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  // Check for upload=true query param to auto-open upload modal
  useEffect(() => {
    if (hasProcessedParams.current) return;

    const uploadParam = searchParams.get('upload');
    if (uploadParam === 'true') {
      setIsUploadOpen(true);
      hasProcessedParams.current = true;
      searchParams.delete('upload');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle file upload
  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned upload URL
      const uploadUrlResponse = await apiClient.post('/api/documents/generate_upload_url/', {
        filename: uploadFile.name,
        content_type: uploadFile.type || 'application/octet-stream',
        title: uploadTitle || uploadFile.name,
        description: uploadDescription,
        business: uploadBusinessId || null,
        project: uploadProjectId || null,
        task: uploadTaskId || null,
        categories: uploadCategoryIds,
      });

      const uploadData: DocumentUploadResponse = uploadUrlResponse.data;
      setUploadProgress(20);

      // Step 2: Upload file to S3 or local endpoint
      if (uploadData.local) {
        // Local storage - use multipart form
        const formData = new FormData();
        formData.append('file', uploadFile);
        await apiClient.post(`/api/documents/${uploadData.document_id}/upload_file/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        // S3 - direct upload with PUT
        await fetch(uploadData.upload_url, {
          method: 'PUT',
          body: uploadFile,
          headers: {
            'Content-Type': uploadFile.type || 'application/octet-stream',
          },
        });
      }
      setUploadProgress(80);

      // Step 3: Mark upload complete
      await apiClient.post(`/api/documents/${uploadData.document_id}/complete_upload/`, {
        file_size: uploadFile.size,
      });
      setUploadProgress(100);

      // Reset and refresh
      setIsUploadOpen(false);
      resetUploadForm();
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Error uploading document:', err);
      setError('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadTitle('');
    setUploadDescription('');
    setUploadBusinessId('');
    setUploadProjectId('');
    setUploadTaskId('');
    setUploadCategoryIds([]);
    setUploadProgress(0);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteDoc) return;

    setDeleting(true);
    try {
      await apiClient.delete(`/api/documents/${deleteDoc.id}/`);
      setDeleteDoc(null);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  // Handle view (open preview modal)
  const handleView = (doc: Document) => {
    setPreviewDoc(doc);
  };

  // Get viewable URL for a document — uses API serve endpoint for local files
  const resolveFileUrl = (doc: Document): string => {
    const url = doc.file_url_signed || doc.file_url;
    // S3 presigned URLs are already absolute
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) return url;
    // Local files: use the API serve endpoint (nginx doesn't serve /media/)
    return `/api/documents/${doc.id}/serve_file/`;
  };

  const getPreviewType = (fileType: string): 'image' | 'pdf' | 'office' | 'text' | 'unsupported' => {
    if (fileType.startsWith('image/')) return 'image';
    if (fileType === 'application/pdf') return 'pdf';
    if (
      fileType.includes('opendocument') ||
      fileType.includes('officedocument') ||
      fileType.includes('msword') ||
      fileType.includes('ms-excel') ||
      fileType.includes('ms-powerpoint') ||
      fileType.includes('excel') ||
      fileType.includes('powerpoint') ||
      fileType.includes('word')
    ) return 'office';
    if (fileType.startsWith('text/') || fileType === 'application/json' || fileType === 'application/xml') return 'text';
    return 'unsupported';
  };

  // Handle download
  const handleDownload = (doc: Document) => {
    const url = resolveFileUrl(doc);
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || doc.title;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <Screen>
      <Section
        title="Documents"
        action={
          <div style={{ display: 'flex', gap: SPACING[12] }}>
            <Button
              variant={showFilters || hasActiveFilters ? 'primary' : 'secondary'}
              onClick={() => setShowFilters(!showFilters)}
              icon={<FunnelIcon style={{ width: '16px', height: '16px' }} />}
            >
              Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v).length})`}
            </Button>
            <Button
              variant="primary"
              onClick={() => setIsUploadOpen(true)}
              icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}
            >
              Upload
            </Button>
          </div>
        }
      >
        {/* Filters */}
        {showFilters && (
          <div style={{
            backgroundColor: COLORS.bg.panel,
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            padding: SPACING[16],
            marginBottom: SPACING[16],
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: SPACING[16],
            }}>
              <h3 style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                fontWeight: '600',
                color: COLORS.text.primary,
                margin: 0,
              }}>
                Filter Documents
              </h3>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: COLORS.accent.default,
                    cursor: 'pointer',
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.small,
                    fontWeight: '500',
                  }}
                >
                  Clear all
                </button>
              )}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: SPACING[16],
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}>
                  Search
                </label>
                <div style={{ position: 'relative' }}>
                  <MagnifyingGlassIcon style={{
                    position: 'absolute',
                    left: SPACING[12],
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '16px',
                    height: '16px',
                    color: COLORS.text.tertiary,
                  }} />
                  <Input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="Search documents..."
                    style={{ paddingLeft: SPACING[32] }}
                  />
                </div>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}>
                  Business
                </label>
                <Select
                  value={filters.businessId}
                  onChange={(e) => handleFilterChange('businessId', e.target.value)}
                  options={[
                    { value: '', label: 'All Businesses' },
                    ...businesses.map(b => ({ value: b.id.toString(), label: b.name })),
                  ]}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}>
                  Project
                </label>
                <Select
                  value={filters.projectId}
                  onChange={(e) => handleFilterChange('projectId', e.target.value)}
                  options={[
                    { value: '', label: 'All Projects' },
                    ...projects.map(p => ({ value: p.id.toString(), label: p.name })),
                  ]}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}>
                  Category
                </label>
                <Select
                  value={filters.categoryId}
                  onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                  options={[
                    { value: '', label: 'All Categories' },
                    ...categories.map(c => ({ value: c.id.toString(), label: c.name })),
                  ]}
                />
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <Alert variant="error" style={{ marginBottom: SPACING[16] }}>
            {error}
          </Alert>
        )}

        {/* Loading state */}
        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[48],
          }}>
            <Spinner size="lg" />
          </div>
        ) : documents.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: SPACING[48],
            color: COLORS.text.secondary,
          }}>
            <DocumentIcon style={{ width: '48px', height: '48px', margin: '0 auto', marginBottom: SPACING[16], opacity: 0.5 }} />
            <p style={{ margin: 0 }}>No documents found. Upload your first document to get started.</p>
          </div>
        ) : (
          /* Document grid */
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: SPACING[16],
          }}>
            {documents.map(doc => (
              <Panel key={doc.id}>
                {/* Image thumbnail preview */}
                {doc.file_type.startsWith('image/') && resolveFileUrl(doc) && (
                  <div
                    style={{
                      marginBottom: SPACING[12],
                      borderRadius: RADIUS,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      backgroundColor: COLORS.bg.base,
                      maxHeight: '160px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={() => handleView(doc)}
                  >
                    <img
                      src={resolveFileUrl(doc)}
                      alt={doc.title}
                      style={{
                        width: '100%',
                        maxHeight: '160px',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: SPACING[12] }}>
                  <div style={{
                    padding: SPACING[8],
                    backgroundColor: COLORS.bg.base,
                    borderRadius: RADIUS,
                    color: COLORS.accent.default,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  onClick={() => handleView(doc)}
                  >
                    {getFileIcon(doc.file_type)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3
                      style={{
                        fontSize: TYPOGRAPHY.body.sizes.default,
                        fontWeight: '600',
                        color: COLORS.accent.default,
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleView(doc)}
                      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      {doc.title}
                    </h3>
                    <p style={{
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.text.tertiary,
                      margin: `${SPACING[4]} 0 0 0`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {doc.file_name} {doc.file_size && `· ${formatFileSize(doc.file_size)}`}
                    </p>
                  </div>
                </div>

                {doc.description && (
                  <p style={{
                    fontSize: TYPOGRAPHY.body.sizes.small,
                    color: COLORS.text.secondary,
                    margin: `${SPACING[12]} 0 0 0`,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {doc.description}
                  </p>
                )}

                {/* Tags */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: SPACING[8],
                  marginTop: SPACING[12],
                }}>
                  {doc.business_name && (
                    <span style={{
                      fontSize: TYPOGRAPHY.body.sizes.tiny,
                      backgroundColor: COLORS.bg.base,
                      color: COLORS.text.secondary,
                      padding: `${SPACING[4]} ${SPACING[8]}`,
                      borderRadius: '9999px',
                    }}>
                      {doc.business_name}
                    </span>
                  )}
                  {doc.project_name && (
                    <span style={{
                      fontSize: TYPOGRAPHY.body.sizes.tiny,
                      backgroundColor: COLORS.bg.base,
                      color: COLORS.text.secondary,
                      padding: `${SPACING[4]} ${SPACING[8]}`,
                      borderRadius: '9999px',
                    }}>
                      {doc.project_name}
                    </span>
                  )}
                  {doc.category_names.map(name => (
                    <span key={name} style={{
                      fontSize: TYPOGRAPHY.body.sizes.tiny,
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      color: COLORS.accent.default,
                      padding: `${SPACING[4]} ${SPACING[8]}`,
                      borderRadius: '9999px',
                    }}>
                      {name}
                    </span>
                  ))}
                </div>

                {/* Actions */}
                <div style={{
                  display: 'flex',
                  gap: SPACING[8],
                  marginTop: SPACING[12],
                  paddingTop: SPACING[12],
                  borderTop: `1px solid ${COLORS.border.default}`,
                }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleView(doc)}
                    icon={<EyeIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    View
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(doc)}
                    icon={<ArrowDownTrayIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Download
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteDoc(doc)}
                    style={{ color: COLORS.status.error }}
                    icon={<TrashIcon style={{ width: '16px', height: '16px' }} />}
                  >
                    Delete
                  </Button>
                </div>
              </Panel>
            ))}
          </div>
        )}
      </Section>

      {/* Upload Modal */}
      <Transition appear show={isUploadOpen} as={Fragment}>
        <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={() => !uploading && setIsUploadOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)' }} />
          </Transition.Child>

          <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
            <div style={{
              display: 'flex',
              minHeight: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              padding: SPACING[16],
            }}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-150"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel style={{
                  width: '100%',
                  maxWidth: '500px',
                  backgroundColor: COLORS.bg.panel,
                  borderRadius: RADIUS,
                  padding: SPACING[24],
                  border: `1px solid ${COLORS.border.default}`,
                }}>
                  <Dialog.Title style={{
                    fontSize: TYPOGRAPHY.headings.sizes.h3,
                    fontWeight: TYPOGRAPHY.headings.weight,
                    color: COLORS.text.primary,
                    margin: 0,
                    marginBottom: SPACING[24],
                  }}>
                    Upload Document
                  </Dialog.Title>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                    {/* File input */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '600',
                        color: COLORS.text.primary,
                        marginBottom: SPACING[8],
                      }}>
                        File *
                      </label>
                      <input
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadFile(file);
                            if (!uploadTitle) setUploadTitle(file.name.replace(/\.[^/.]+$/, ''));
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: SPACING[12],
                          border: `1px solid ${COLORS.border.default}`,
                          borderRadius: RADIUS,
                          backgroundColor: COLORS.bg.base,
                          color: COLORS.text.primary,
                        }}
                      />
                    </div>

                    {/* Title */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '600',
                        color: COLORS.text.primary,
                        marginBottom: SPACING[8],
                      }}>
                        Title
                      </label>
                      <Input
                        value={uploadTitle}
                        onChange={(e) => setUploadTitle(e.target.value)}
                        placeholder="Document title"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '600',
                        color: COLORS.text.primary,
                        marginBottom: SPACING[8],
                      }}>
                        Description
                      </label>
                      <textarea
                        value={uploadDescription}
                        onChange={(e) => setUploadDescription(e.target.value)}
                        placeholder="Optional description..."
                        rows={2}
                        style={{
                          width: '100%',
                          padding: SPACING[12],
                          border: `1px solid ${COLORS.border.default}`,
                          borderRadius: RADIUS,
                          backgroundColor: COLORS.bg.base,
                          color: COLORS.text.primary,
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.default,
                          resize: 'vertical',
                        }}
                      />
                    </div>

                    {/* Business */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '600',
                        color: COLORS.text.primary,
                        marginBottom: SPACING[8],
                      }}>
                        Business
                      </label>
                      <Select
                        value={uploadBusinessId}
                        onChange={(e) => setUploadBusinessId(e.target.value)}
                        options={[
                          { value: '', label: 'None' },
                          ...businesses.map(b => ({ value: b.id.toString(), label: b.name })),
                        ]}
                      />
                    </div>

                    {/* Project */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '600',
                        color: COLORS.text.primary,
                        marginBottom: SPACING[8],
                      }}>
                        Project
                      </label>
                      <Select
                        value={uploadProjectId}
                        onChange={(e) => setUploadProjectId(e.target.value)}
                        options={[
                          { value: '', label: 'None' },
                          ...projects.map(p => ({ value: p.id.toString(), label: p.name })),
                        ]}
                      />
                    </div>

                    {/* Task */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '600',
                        color: COLORS.text.primary,
                        marginBottom: SPACING[8],
                      }}>
                        Task
                      </label>
                      <Select
                        value={uploadTaskId}
                        onChange={(e) => setUploadTaskId(e.target.value)}
                        options={[
                          { value: '', label: 'None' },
                          ...tasks.map(t => ({
                            value: t.id.toString(),
                            label: t.description.length > 50 ? t.description.slice(0, 50) + '...' : t.description
                          })),
                        ]}
                      />
                    </div>

                    {/* Upload progress */}
                    {uploading && (
                      <div>
                        <div style={{
                          height: '4px',
                          backgroundColor: COLORS.bg.base,
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${uploadProgress}%`,
                            backgroundColor: COLORS.accent.default,
                            transition: 'width 0.3s ease',
                          }} />
                        </div>
                        <p style={{
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          color: COLORS.text.secondary,
                          textAlign: 'center',
                          marginTop: SPACING[8],
                        }}>
                          Uploading... {uploadProgress}%
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: SPACING[12],
                    marginTop: SPACING[24],
                  }}>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setIsUploadOpen(false);
                        resetUploadForm();
                      }}
                      disabled={uploading}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleUpload}
                      disabled={!uploadFile || uploading}
                      isLoading={uploading}
                    >
                      Upload
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Preview Modal */}
      <Transition appear show={!!previewDoc} as={Fragment}>
        <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={() => setPreviewDoc(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)' }} />
          </Transition.Child>

          <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
            <div style={{
              display: 'flex',
              minHeight: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              padding: SPACING[16],
            }}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-150"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel style={{
                  width: '100%',
                  maxWidth: previewDoc && getPreviewType(previewDoc.file_type) === 'image' ? '900px' : '900px',
                  maxHeight: '90vh',
                  backgroundColor: COLORS.bg.panel,
                  borderRadius: RADIUS,
                  border: `1px solid ${COLORS.border.default}`,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}>
                  {/* Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    borderBottom: `1px solid ${COLORS.border.default}`,
                    flexShrink: 0,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12], minWidth: 0 }}>
                      <div style={{ color: COLORS.accent.default, flexShrink: 0 }}>
                        {previewDoc && getFileIcon(previewDoc.file_type)}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <h3 style={{
                          fontSize: TYPOGRAPHY.body.sizes.default,
                          fontWeight: '600',
                          color: COLORS.text.primary,
                          margin: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {previewDoc?.title}
                        </h3>
                        <p style={{
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          color: COLORS.text.tertiary,
                          margin: 0,
                        }}>
                          {previewDoc?.file_name} {previewDoc?.file_size && `· ${formatFileSize(previewDoc.file_size)}`}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8], flexShrink: 0 }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => previewDoc && handleDownload(previewDoc)}
                        icon={<ArrowDownTrayIcon style={{ width: '16px', height: '16px' }} />}
                      >
                        Download
                      </Button>
                      <button
                        onClick={() => setPreviewDoc(null)}
                        style={{
                          padding: SPACING[4],
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderRadius: RADIUS,
                          cursor: 'pointer',
                          color: COLORS.text.tertiary,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = COLORS.bg.hover;
                          e.currentTarget.style.color = COLORS.text.primary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = COLORS.text.tertiary;
                        }}
                      >
                        <XMarkIcon style={{ width: '20px', height: '20px' }} />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div style={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    backgroundColor: COLORS.bg.base,
                  }}>
                    {previewDoc && (() => {
                      const type = getPreviewType(previewDoc.file_type);
                      const fileUrl = resolveFileUrl(previewDoc);

                      if (type === 'image' && fileUrl) {
                        return (
                          <img
                            src={fileUrl}
                            alt={previewDoc.title}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '75vh',
                              objectFit: 'contain',
                            }}
                          />
                        );
                      }

                      if (type === 'pdf' && fileUrl) {
                        return (
                          <iframe
                            src={fileUrl}
                            title={previewDoc.title}
                            style={{
                              width: '100%',
                              height: '75vh',
                              border: 'none',
                            }}
                          />
                        );
                      }

                      if (type === 'office' && fileUrl) {
                        const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`;
                        return (
                          <iframe
                            src={viewerUrl}
                            title={previewDoc.title}
                            style={{
                              width: '100%',
                              height: '75vh',
                              border: 'none',
                            }}
                          />
                        );
                      }

                      // Fallback: show extracted text or file info
                      return (
                        <div style={{
                          padding: SPACING[24],
                          width: '100%',
                          maxHeight: '75vh',
                          overflowY: 'auto',
                        }}>
                          {previewDoc.extracted_text ? (
                            <div>
                              <p style={{
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                fontWeight: '600',
                                color: COLORS.text.secondary,
                                margin: `0 0 ${SPACING[12]} 0`,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                              }}>
                                Extracted Text
                              </p>
                              <pre style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                color: COLORS.text.primary,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                margin: 0,
                                lineHeight: 1.6,
                              }}>
                                {previewDoc.extracted_text}
                              </pre>
                            </div>
                          ) : (
                            <div style={{ textAlign: 'center', padding: SPACING[32] }}>
                              <DocumentIcon style={{
                                width: '48px',
                                height: '48px',
                                margin: '0 auto',
                                marginBottom: SPACING[16],
                                color: COLORS.text.tertiary,
                                opacity: 0.5,
                              }} />
                              <p style={{
                                color: COLORS.text.secondary,
                                margin: `0 0 ${SPACING[4]} 0`,
                              }}>
                                Preview not available for this file type
                              </p>
                              <p style={{
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                color: COLORS.text.tertiary,
                                margin: 0,
                              }}>
                                {previewDoc.file_type} · Download to view this file
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Summary (if available) */}
                  {previewDoc?.summary && (
                    <div style={{
                      padding: `${SPACING[12]} ${SPACING[16]}`,
                      borderTop: `1px solid ${COLORS.border.default}`,
                      flexShrink: 0,
                    }}>
                      <p style={{
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '600',
                        color: COLORS.text.secondary,
                        margin: `0 0 ${SPACING[4]} 0`,
                      }}>
                        Summary
                      </p>
                      <p style={{
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        color: COLORS.text.primary,
                        margin: 0,
                        lineHeight: 1.5,
                      }}>
                        {previewDoc.summary}
                      </p>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Confirmation Modal */}
      <Transition appear show={!!deleteDoc} as={Fragment}>
        <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={() => !deleting && setDeleteDoc(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)' }} />
          </Transition.Child>

          <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
            <div style={{
              display: 'flex',
              minHeight: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              padding: SPACING[16],
            }}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-150"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel style={{
                  width: '100%',
                  maxWidth: '400px',
                  backgroundColor: COLORS.bg.panel,
                  borderRadius: RADIUS,
                  padding: SPACING[24],
                  border: `1px solid ${COLORS.border.default}`,
                }}>
                  <Dialog.Title style={{
                    fontSize: TYPOGRAPHY.headings.sizes.h3,
                    fontWeight: TYPOGRAPHY.headings.weight,
                    color: COLORS.text.primary,
                    margin: 0,
                    marginBottom: SPACING[16],
                  }}>
                    Delete Document
                  </Dialog.Title>

                  <p style={{
                    color: COLORS.text.secondary,
                    margin: 0,
                  }}>
                    Are you sure you want to delete "{deleteDoc?.title}"? This action cannot be undone.
                  </p>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: SPACING[12],
                    marginTop: SPACING[24],
                  }}>
                    <Button
                      variant="secondary"
                      onClick={() => setDeleteDoc(null)}
                      disabled={deleting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleDelete}
                      disabled={deleting}
                      isLoading={deleting}
                    >
                      Delete
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Screen>
  );
};

export default DocumentsPage;
